/**
 * Estructura agrupada del sidebar (Entrega 1 del refactor UX).
 *
 * Cada grupo tiene una `key` única que sirve como módulo opt-in en
 * `restaurants.enabled_modules`. El owner puede activar/desactivar grupos
 * desde Configuración → Módulos.
 *
 * Onboarding gradual: si el restaurante NO tiene `enabled_modules` definido,
 * se calcula por defecto a partir de la antigüedad (`computeDefaultEnabledModules`).
 *
 * Algunas entradas son "comingSoon": aparecen visibles pero deshabilitadas y
 * con tooltip "Disponible próximamente". Se construirán en la Entrega 2.
 *
 * `description` (opcional): aparece como tooltip en hover sobre cada grupo
 * o item, ayudando al usuario nuevo a entender la taxonomía sin tener que
 * clickear. Si no se define para un item, el tooltip muestra solo el label.
 */
import {
  LayoutDashboard, BookOpen, UtensilsCrossed, ChefHat,
  Package, Truck, ClipboardList, PackageCheck, TrendingUp,
  Wallet, ShoppingCart, Users,
  BarChart2, Calculator,
  Globe, Share2,
  Settings, UserCog, Plug, Coins, HelpCircle, Home,
} from "lucide-react";

/**
 * @typedef {Object} NavItem
 * @property {string} path - ruta destino
 * @property {Function} icon - lucide icon component
 * @property {string} label - texto visible
 * @property {string} [description] - tooltip descriptivo en hover
 * @property {boolean} [exact] - si la ruta debe coincidir exacta
 * @property {string[]} roles - roles que pueden ver el ítem
 * @property {string} moduleKey - clave del feature flag de plan
 * @property {boolean} [comingSoon] - si es true, se muestra deshabilitado
 *
 * @typedef {Object} NavGroup
 * @property {string} key - identificador estable del grupo
 * @property {string} label - texto del header
 * @property {string} [description] - tooltip descriptivo en hover
 * @property {Function} icon - lucide icon
 * @property {NavItem[]} items
 * @property {boolean} [pinned] - si está pinned arriba (Inicio) o abajo (Configuración)
 */

const ALL_OPERATIONAL = ["owner", "manager", "accountant", "kitchen", "waiter", "social_media"];

export const NAV_GROUPS = [
  {
    key: "home",
    label: "Inicio",
    description: "Panel principal con widgets y resumen del negocio.",
    icon: Home,
    pinned: "top",
    items: [
      {
        path: "/app", icon: LayoutDashboard, label: "Panel de Control", exact: true,
        description: "Resumen ejecutivo con KPIs, alertas de coste y actividad reciente.",
        roles: ["owner", "manager", "accountant"], moduleKey: "dashboard",
      },
    ],
  },
  {
    key: "kitchen",
    label: "Carta y cocina",
    description: "Platos y cartas que ve el cliente, recetas con escandallo, producción diaria.",
    icon: ChefHat,
    items: [
      {
        path: "/app/menus", icon: BookOpen, label: "Carta y platos",
        description: "Gestiona cartas, secciones y platos. Edita precios y descripciones.",
        roles: ["owner", "manager", "kitchen", "waiter", "accountant"], moduleKey: "menus",
      },
      {
        path: "/app/recipes", icon: UtensilsCrossed, label: "Recetas y escandallo",
        description: "Recetas simples o profesionales con bases reutilizables. IA, costes y márgenes.",
        roles: ["owner", "manager", "kitchen"], moduleKey: "recipes",
      },
      {
        path: "/app/production", icon: ChefHat, label: "Producción",
        description: "Registra lotes preparados (mise en place), caducidades y consumo de bases.",
        roles: ["owner", "manager", "kitchen"], moduleKey: "recipes",
      },
    ],
  },
  {
    key: "stock",
    label: "Almacén y compras",
    description: "Inventario en tiempo real, pedidos a proveedores, recepciones y previsión IA.",
    icon: Package,
    items: [
      {
        path: "/app/inventory", icon: Package, label: "Inventario",
        description: "Stock actual, movimientos, auto-reorder sugerido por IA.",
        roles: ["owner", "manager", "kitchen"], moduleKey: "inventory",
      },
      {
        path: "/app/shopping-lists", icon: ClipboardList, label: "Lista de compras",
        description: "Listas ad-hoc de compra directa en súper o cash&carry (no B2B formal).",
        roles: ["owner", "manager", "kitchen"], moduleKey: "shopping_lists",
      },
      {
        path: "/app/orders", icon: ShoppingCart, label: "Pedidos",
        description: "Pedidos formales a proveedores: borrador, enviado, recibido, con historial.",
        roles: ["owner", "manager"], moduleKey: "shopping_lists",
      },
      {
        path: "/app/receptions", icon: PackageCheck, label: "Recepciones",
        description: "Confirma la mercancía recibida. OCR del albarán y discrepancias del pedido.",
        roles: ["owner", "manager", "kitchen"], moduleKey: "inventory",
      },
      {
        path: "/app/suppliers", icon: Truck, label: "Proveedores",
        description: "Fichas de proveedores, scorecards, recordatorios y condiciones de pago.",
        roles: ["owner", "manager"], moduleKey: "suppliers",
      },
      {
        path: "/app/forecast", icon: TrendingUp, label: "Previsiones (IA)",
        description: "Previsión de demanda por plato para preparar sin excedentes ni roturas.",
        roles: ["owner", "manager", "accountant"], moduleKey: "forecast",
      },
    ],
  },
  {
    key: "sales",
    label: "Ventas y caja",
    description: "Tickets del día, apertura y cierre de caja, clientes y fidelización.",
    icon: Wallet,
    items: [
      {
        path: "/app/sales-today", icon: BarChart2, label: "Ventas del día",
        description: "Tickets en vivo, top platos, comparativa con días anteriores.",
        roles: ["owner", "manager", "waiter"], moduleKey: "tpv",
      },
      {
        path: "/app/cash", icon: Wallet, label: "Caja y Cierres",
        description: "Apertura y cierre de caja, arqueo, cuadre diario y reportes contables.",
        roles: ["owner", "manager", "waiter"], moduleKey: "finance",
      },
      {
        path: "/app/customers", icon: Users, label: "Clientes y fidelización",
        description: "CRM con histórico de visitas, tarjetas de fidelidad y campañas.",
        roles: ["owner", "manager"], moduleKey: "customers",
      },
    ],
  },
  {
    key: "finance",
    label: "Finanzas y análisis",
    description: "Analítica del negocio, contabilidad con IA, IVA y facturas escaneadas.",
    icon: BarChart2,
    items: [
      {
        path: "/app/analytics", icon: BarChart2, label: "Análisis del negocio",
        description: "KPIs avanzados de ventas, márgenes y rendimiento operativo.",
        roles: ["owner", "manager", "accountant"], moduleKey: "analytics",
      },
      {
        path: "/app/finance", icon: Calculator, label: "Contabilidad e IVA",
        description: "Facturas escaneadas por IA, conciliación, reportes de IVA y bandeja fiscal.",
        roles: ["owner", "manager", "accountant"], moduleKey: "finance",
      },
    ],
  },
  {
    key: "digital",
    label: "Presencia digital",
    description: "Página web pública y gestión de redes sociales del restaurante.",
    icon: Globe,
    items: [
      {
        path: "/app/website", icon: Globe, label: "Página web",
        description: "Web pública con reservas, menú dinámico y SEO local automatizado.",
        roles: ["owner", "manager"], moduleKey: "website",
      },
      {
        path: "/app/social", icon: Share2, label: "Redes sociales",
        description: "Programación de posts con IA, imágenes y analítica social.",
        roles: ["owner", "social_media", "manager"], moduleKey: "social",
      },
    ],
  },
  {
    key: "admin",
    label: "Configuración",
    description: "Ajustes del restaurante, equipo, integraciones y plan de suscripción.",
    icon: Settings,
    pinned: "bottom",
    items: [
      {
        path: "/app/settings", icon: Settings, label: "General",
        description: "Datos del restaurante, módulos activos, preferencias.",
        roles: ["owner"], moduleKey: "settings",
      },
      {
        path: "/app/team", icon: UserCog, label: "Equipo",
        description: "Miembros del equipo, roles y permisos finos (caja, contabilidad).",
        roles: ["owner", "manager"], moduleKey: "team",
      },
      {
        path: "/app/tpv", icon: Plug, label: "Integración TPV",
        description: "Conecta tu sistema de punto de venta para sincronizar ventas y tickets.",
        roles: ["owner", "manager", "accountant"], moduleKey: "tpv",
      },
      {
        path: "/app/subscription", icon: Coins, label: "Plan y créditos",
        description: "Plan activo, créditos IA, historial de consumo y top-ups.",
        roles: ["owner"], moduleKey: "subscription",
      },
      {
        path: "/app/help", icon: HelpCircle, label: "Ayuda",
        description: "Tutoriales, FAQs y canal de soporte.",
        roles: ALL_OPERATIONAL, moduleKey: "help",
      },
    ],
  },
];

/**
 * Onboarding gradual: a partir de la antigüedad del restaurante (en días)
 * decidimos qué grupos están activados por defecto cuando el owner aún no
 * los ha tocado en Configuración.
 *
 * day  0:  home + kitchen + stock + admin              (4 grupos)
 * day  7:  + sales                                     (5 grupos)
 * day 30:  + finance                                   (6 grupos)
 * day 60:  + digital                                   (7 grupos, completo)
 */
export function computeDefaultEnabledModules(daysSinceCreation) {
  const d = Math.max(0, Number(daysSinceCreation) || 0);
  const base = ["home", "kitchen", "stock", "admin"];
  if (d < 7) return base;
  if (d < 30) return [...base, "sales"];
  if (d < 60) return [...base, "sales", "finance"];
  return [...base, "sales", "finance", "digital"];
}

export const ALL_MODULE_KEYS = NAV_GROUPS.map((g) => g.key);
